"use client"
import { Dialog, DialogContent, DialogOverlay, DialogTrigger } from "@/components"
import { DialogHeader } from "@/components/Dialog/DialogHeader"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { toast } from "@/components/Toast"
import { Avatar, Button, IconButton, SearchInput, Spinner, Text, TextArea } from "@/elements"
import { graphql } from "@/gql"
import { GetShareSheetRecipientsDocument } from "@/gql/graphql"
import { useHandleListEndReached } from "@/hooks/useHandleListEndReached"
import { CheckIcon } from "@/icons/outline"
import { copyToClipboard } from "@/utils/copyToClipboard"
import { useLazyQuery } from "@apollo/client"
import { ArrowUpTrayIcon, Square2StackIcon } from "@heroicons/react/24/outline"
import { debounce } from "lodash"
import { useEffect, useMemo, useRef, useState } from "react"
import { Channel } from "stream-chat"
import { useCreateChatClient } from "stream-chat-react"

const apiKey = process.env.NEXT_PUBLIC_STREAM_CHAT_API_KEY!

graphql(`
  query GetShareSheetRecipients($first: Int, $after: String, $filters: IndividualCreativeFilter, $name: String) {
    contacts: allIndividualCreatives(first: $first, after: $after, filters: $filters, name: $name) {
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
      edges {
        cursor
        node {
          id
          name
          avatarUrl(width: 240)
          avatarInitials
          displayName
          user {
            id
            uuid
            name
          }
        }
      }
    }
  }
`)

interface Props {
  Trigger?: React.ReactNode
  jobId: string | null | undefined
  messagingToken: string
  userUUID: string
}

const PAGE_SIZE = 12

export const ShareJobModal: React.FC<Props> = props => {
  const [isOpen, setIsOpen] = useState(false)
  const [messageValue, setMessageValue] = useState("")
  const { userUUID, messagingToken } = props
  const [nameSearch, setNameSearch] = useState("")
  const [preFetchData, { data, loading: loadingSearch, refetch, networkStatus, fetchMore }] = useLazyQuery(
    GetShareSheetRecipientsDocument,
    {
      variables: {
        first: PAGE_SIZE,
        filters: {
          isConnectedWith: true,
        },
      },
      context: { version: 2 },
    }
  )
  const debouncedRefetch = useMemo(
    () =>
      debounce((value: string) => {
        refetch({
          filters: {
            isConnectedWith: true,
          },
          name: value,
        })
      }, 300),
    [refetch]
  )

  useEffect(() => {
    if (!isOpen) return

    debouncedRefetch(nameSearch)
    return () => {
      debouncedRefetch.cancel()
    }
  }, [nameSearch, debouncedRefetch])

  const loadMoreRef = useRef(null)

  useHandleListEndReached({
    pageInfo: data?.contacts.pageInfo,
    first: PAGE_SIZE,
    networkStatus,
    fetchMore,
    observerRef: loadMoreRef,
  })

  const [selectedUser, setSelectedUser] = useState<string | undefined>()

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setNameSearch("")
        setMessageValue("")
        setSelectedUser(undefined)
      }, 300)
    }
  }, [isOpen])

  const client = useCreateChatClient({
    apiKey,
    tokenOrProvider: messagingToken,
    userData: { id: userUUID },
  })

  const handleSendOrCreateMessage = async ({ messageToUUID }: { messageToUUID: string }) => {
    if (!client) return

    const channelType = "creative_networking"
    let channel = null

    try {
      const existingChannels = await client.queryChannels({
        members: {
          $eq: [client.userID!, messageToUUID],
        },
      })
      // FIXME: For now we needt to filter on the frontend because stream timesout with a filter
      const channels = existingChannels.filter((channel: Channel) => channel.type === channelType)

      channel = channels[0]
    } catch (e) {
      channel = client.channel(channelType, {
        members: [client.userID!, messageToUUID],
      })
    }

    if (channel) {
      channel
        .sendMessage({
          text: messageValue,
          attachments: [
            {
              type: "job_posting",
              id: props.jobId,
            },
          ],
        })
        .then(() => {
          setIsOpen(false)

          toast.success("Shared job")
        })
        .catch(() => {
          toast.error("Failed to share job")
        })
    }
  }

  /* dont need to change this to ControlledModal, custom footer */
  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger onMouseEnter={() => preFetchData()} asChild>
        {props.Trigger ? props.Trigger : <IconButton Icon={ArrowUpTrayIcon} size="large" variant="white" />}
      </DialogTrigger>
      <DialogOverlay />
      <DialogContent className="min-w-[616px]">
        <div>
          <DialogHeader title="Share job" />

          <>
            <div className="px-6">
              <SearchInput onChange={e => setNameSearch(e.currentTarget.value)} value={nameSearch} className="w-full" />
            </div>
            {!loadingSearch && data?.contacts.totalCount === 0 && nameSearch !== "" ? (
              <EmptyState
                title="No connection"
                description={`No connections found for "${nameSearch}"`}
                borderless={true}
              />
            ) : null}
            <div className="grid max-h-[346px] grid-cols-4 gap-x-2 gap-y-6 overflow-y-auto px-6 pb-6 pt-8">
              {loadingSearch && !data
                ? Array(8)
                    .fill(0)
                    .map((_, i) => <Skeleton key={i} />)
                : data?.contacts.edges.map((contact, index) => (
                    <div
                      key={index}
                      className="flex h-[126px] cursor-pointer flex-col items-center gap-y-4"
                      onClick={() => {
                        if (selectedUser === contact?.node.user.uuid) {
                          setSelectedUser(undefined)
                        } else {
                          setSelectedUser(contact?.node.user.uuid)
                        }
                      }}>
                      <div className="relative">
                        <Avatar initials={contact?.node.avatarInitials} src={contact?.node.avatarUrl} size="2xl" />
                        {selectedUser === contact?.node.user.uuid ? (
                          <div className="absolute bottom-0 right-0 flex size-6 shrink-0 items-center justify-center rounded-full bg-bg-1">
                            <div className="flex size-[18px] items-center justify-center rounded-full bg-fg-primary">
                              <CheckIcon className="size-[10px] stroke-bg-1" />
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <Text maxLines={2} variant="label" className="text-center">
                        {contact?.node.displayName || contact?.node.user.name}
                      </Text>
                    </div>
                  ))}
              <div
                className="relative mb-1 mt-2 flex h-[20px] w-full grid-cols-4 flex-row items-center justify-center"
                ref={loadMoreRef}>
                {data?.contacts?.pageInfo?.hasNextPage && <Spinner size="medium" />}
              </div>
            </div>
          </>

          <div className="h-[1px] w-full bg-bg-3"></div>
          {selectedUser ? (
            <div className="flex flex-col gap-y-4 px-6 pb-6 pt-4">
              <TextArea
                onChange={e => setMessageValue(e.currentTarget.value)}
                value={messageValue}
                className="w-full"
                rows={1}
                placeholder="Write a message"
              />
              <Button onClick={() => handleSendOrCreateMessage({ messageToUUID: selectedUser })} className="">
                Send
              </Button>
            </div>
          ) : (
            <div
              onClick={() => {
                copyToClipboard(process.env.NEXT_PUBLIC_VERCEL_URL + `/jobs/${props.jobId}`)
              }}
              className="mx-auto flex w-fit cursor-pointer flex-col items-center justify-center gap-y-4 px-6 py-[18px]">
              <IconButton Icon={Square2StackIcon} />
              <Text variant="CTASmall">Copy link</Text>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

const Skeleton = () => {
  return (
    <div className="flex h-[126px] cursor-pointer flex-col items-center gap-y-4">
      <Avatar size="2xl" />
    </div>
  )
}
